import { Container, Box, Typography, Divider, Link, Paper, Chip, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const EFFECTIVE_DATE = '29 June 2026';
const PLATFORM_NAME = 'EncycGames';
const CONTACT_EMAIL = 'privacy@encycgames.com';
const DPO_EMAIL = 'dpo@encycgames.com';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ mb: 1.2, lineHeight: 1.8 }}>
    {children}
  </Typography>
);

const PrivacyPolicyPage = () => (
  <Container maxWidth="md">
    <Box sx={{ mt: 5, mb: 6 }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
        {/* Header */}
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
          Privacy Policy &amp; Terms of Service
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip label={`Effective: ${EFFECTIVE_DATE}`} size="small" color="primary" variant="outlined" />
          <Chip label="GDPR" size="small" />
          <Chip label="COPPA" size="small" />
          <Chip label="DPDP Act 2023" size="small" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please read this policy carefully before registering or using {PLATFORM_NAME}.
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* 1. Who we are */}
        <Section title="1. Who We Are">
          <P>
            <strong>{PLATFORM_NAME}</strong> ("we", "us", "our") is a sports performance tracking and
            contest platform. We are the data controller for personal data collected on this platform.
            For privacy questions, contact <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
            For GDPR Data Protection Officer enquiries: <Link href={`mailto:${DPO_EMAIL}`}>{DPO_EMAIL}</Link>.
          </P>
        </Section>

        {/* 2. Data we collect */}
        <Section title="2. Personal Data We Collect">
          <P><strong>All users:</strong> name, email address, date of birth, gender, country, hashed password, login timestamps, IP address.</P>
          <P><strong>Players:</strong> sport performance videos, scores, ratings, coach feedback, contest submissions, age category, academy affiliation.</P>
          <P><strong>Players under 18:</strong> parent/guardian name, parent/guardian email and phone, country, and the parental consent record (timestamp, IP, version).</P>
          <P><strong>Sponsors:</strong> organisation name, sponsorship interests, contact details.</P>
          <P><strong>Coaches:</strong> coaching credentials, academy association, player relationships.</P>
          <P>We do <strong>not</strong> collect payment card details directly — payments are processed by Razorpay or Stripe under their own privacy terms.</P>
        </Section>

        {/* 3. Legal basis */}
        <Section title="3. Legal Basis for Processing (GDPR Art. 6)">
          <P>
            <strong>Consent (Art. 6(1)(a)):</strong> Performance tracking, contest participation, and marketing
            communications. You may withdraw consent at any time — see Section 7.
          </P>
          <P>
            <strong>Contract (Art. 6(1)(b)):</strong> Processing necessary to provide the services you register for.
          </P>
          <P>
            <strong>Legitimate interests (Art. 6(1)(f)):</strong> Platform security, fraud prevention, and system analytics.
          </P>
          <P>
            <strong>Children under 13 (COPPA / GDPR-K):</strong> We require verified parental/guardian consent before
            any personal data of a child under 13 (or under 16 in certain EU member states) is collected.
            Registration for players under 18 requires a parent/guardian to provide their own identity and
            give explicit, informed consent on behalf of the child.
          </P>
        </Section>

        {/* 4. How we use data */}
        <Section title="4. How We Use Your Data">
          <P>— Display your performance analytics to you, your coach, and (where applicable) your academy.</P>
          <P>— Enable contest video submission, scoring, leaderboard ranking, and prize processing.</P>
          <P>— Allow sponsors to view aggregated (non-identifying) contest stats for partnership decisions.</P>
          <P>— Send transactional emails (registration confirmation, password reset, contest result).</P>
          <P>— Send marketing emails <em>only if</em> you have opted in (marketing consent checkbox at registration).</P>
          <P>— Improve platform features via anonymised usage analytics.</P>
        </Section>

        {/* 5. Data sharing */}
        <Section title="5. Data Sharing &amp; Third Parties">
          <P>We do <strong>not</strong> sell personal data. We share data only with:</P>
          <P>— <strong>Coaches / Academy admins</strong> assigned to a player's account (your performance data).</P>
          <P>— <strong>Razorpay / Stripe</strong> for payment processing (name, email, amount — no card numbers stored by us).</P>
          <P>— <strong>AWS S3</strong> for secure video storage (contest submissions are encrypted at rest).</P>
          <P>— <strong>Email providers</strong> (SendGrid / ZeptoMail) for transactional and opted-in marketing email.</P>
          <P>— <strong>Law enforcement</strong> when legally required.</P>
          <P>All third-party processors are bound by Data Processing Agreements compliant with GDPR Chapter V.</P>
        </Section>

        {/* 6. Children / COPPA */}
        <Section title="6. Children's Privacy (COPPA &amp; GDPR-K)">
          <P>
            Players under 18 may only register with the explicit, verifiable consent of a parent or legal guardian.
            The guardian must:
          </P>
          <P>— Provide their full legal name, email, and phone number.</P>
          <P>— Tick the parental consent checkbox acknowledging data processing of their child's personal data.</P>
          <P>— Accept the Terms of Service on behalf of their child.</P>
          <P>
            We record the guardian's IP address, browser user-agent, timestamp, and policy version at the time consent
            is given. This record is stored immutably in our consent audit log and retained for as long as the account is active plus 3 years.
          </P>
          <P>
            A parent/guardian may withdraw consent at any time by emailing <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
            Upon withdrawal we will delete the child's account and all associated personal data within 30 days, except
            data we are legally required to retain.
          </P>
          <P>
            <strong>Players under 13 (US — COPPA):</strong> We do not knowingly collect data from children under 13
            without verified parental consent. If you believe a child under 13 has registered without consent,
            contact <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link> immediately.
          </P>
        </Section>

        {/* 7. Your rights */}
        <Section title="7. Your Rights (GDPR / DPDP)">
          <P>Under GDPR and India's Digital Personal Data Protection Act 2023, you have the right to:</P>
          <P>— <strong>Access</strong> a copy of your personal data (Art. 15 / DPDP § 11).</P>
          <P>— <strong>Rectification</strong> of inaccurate data (Art. 16 / DPDP § 12).</P>
          <P>— <strong>Erasure</strong> ("right to be forgotten") (Art. 17 / DPDP § 13).</P>
          <P>— <strong>Restrict processing</strong> in certain circumstances (Art. 18).</P>
          <P>— <strong>Data portability</strong> in machine-readable format (Art. 20 / DPDP § 11).</P>
          <P>— <strong>Object</strong> to processing based on legitimate interests (Art. 21).</P>
          <P>— <strong>Withdraw consent</strong> at any time without affecting lawfulness of prior processing (Art. 7(3)).</P>
          <P>
            To exercise any right, email <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>. We will
            respond within 30 days. You may also lodge a complaint with your national data protection authority.
          </P>
        </Section>

        {/* 8. Retention */}
        <Section title="8. Data Retention">
          <P>— Active accounts: data retained while account is active and for 3 years after deletion request.</P>
          <P>— Contest videos: retained for the duration of the contest season plus 1 year, then deleted unless you opt to keep them.</P>
          <P>— Consent audit logs: retained for 7 years (legal compliance).</P>
          <P>— Payment records: retained for 7 years (financial/tax obligation).</P>
        </Section>

        {/* 9. Security */}
        <Section title="9. Security">
          <P>
            Passwords are hashed using bcrypt (work factor 10+). Videos are stored on AWS S3 with server-side encryption.
            All traffic is served over HTTPS/TLS. We conduct periodic security reviews and have a responsible disclosure policy.
          </P>
        </Section>

        {/* 10. Terms */}
        <Section title="10. Terms of Service">
          <P>
            By registering on {PLATFORM_NAME}, you agree to use the platform lawfully, respect other users,
            and only submit content (including contest videos) that you own or have rights to. You must not
            submit videos containing harmful, illegal, or inappropriate content.
          </P>
          <P>
            We reserve the right to suspend or delete accounts that violate these terms. Contest entries may
            be disqualified for rule violations. Scores and leaderboard placements are determined solely by
            admin reviewers and are final unless an appeal is raised within 7 days.
          </P>
        </Section>

        {/* 11. Changes */}
        <Section title="11. Changes to This Policy">
          <P>
            We will notify you by email before any material changes to this policy take effect and will
            require renewed consent where required by law. The effective date at the top of this page
            always reflects the latest version.
          </P>
        </Section>

        <Divider sx={{ my: 3 }} />
        <Typography variant="caption" color="text.secondary">
          Last updated: {EFFECTIVE_DATE}. Version 1.0.{' '}
          Questions? <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Link component={RouterLink} to="/register/player" sx={{ mr: 3 }}>
            Register as Player
          </Link>
          <Link component={RouterLink} to="/login">
            Sign in
          </Link>
        </Box>
      </Paper>
    </Box>
  </Container>
);

export default PrivacyPolicyPage;
